import React, {useEffect} from 'react';
import {
	IonButton,
	IonContent,
	IonHeader,
	IonButtons,
	IonBackButton,
	IonPage,
	IonIcon,
	IonToolbar,
	IonGrid,
	IonRow,
	IonCol,
	IonCardTitle,
	IonLabel,
	IonInput,
	IonCard,
	IonCardHeader,
	IonCardContent,
	useIonLoading
} from '@ionic/react';
import useAuth from '../hooks/useAuth';
import './Login.css';
import {arrowBack, shapesOutline} from "ionicons/icons";
import {useMutation} from "@apollo/client";
import {INITIATE_PASSWORD_RESET} from "../schemas/resetpassword";
import {showWebixError, showWebixSuccess} from "../utils";

const webix: any = (window as any).webix;

type LoginState = 'LOGIN' | 'RESET' | 'LOGGED_IN';

const Login: React.FC<any> = () => {
	const {onLogin, token, isSSO, onLogout} = useAuth();
	const [user, setUser] = React.useState<string>("");
	const [password, setPassword] = React.useState<string | null | undefined>("");
	const [presentLoading, dismissLoading] = useIonLoading();
	const [passwordResetEmail, setPasswordResetEmail] = React.useState<string>('');
	const [loginState, setLoginState] = React.useState<LoginState>('LOGIN');
	const [initiatePasswordResetMutation] = useMutation(INITIATE_PASSWORD_RESET);

	const handleLogin = async () => {
		presentLoading({message: 'Logging you in...'});
		try {
			await onLogin(user, password);
		} catch (error) {
			console.error(error);
			showWebixError(webix.template.escape(error));
		} finally {
			dismissLoading();
		}
	}

	const handleForgotPassword = async () => {
		setLoginState('RESET');
		setPasswordResetEmail(user);
	}

	const sendPasswordResetEmail = async () => {
		try {
			presentLoading();
			const initiatePasswordResetResults = (await initiatePasswordResetMutation({
				variables: {
					email: passwordResetEmail
				}
			}))?.data?.initiatePasswordReset;
			if (!initiatePasswordResetResults?.success) {
				showWebixError(initiatePasswordResetResults?.error ?? 'An unknown error occurred');
			} else {
				showWebixSuccess('A password reset email has been sent to the provided email with further instructions.');
				setLoginState('LOGIN');
			}
		} catch (e: any) {
			console.error(e);
			showWebixError(e?.message ?? 'An unknown error occurred');
		} finally {
			dismissLoading();
		}
	}

	useEffect(() => {
		if (token) {
			setLoginState('LOGGED_IN');
		} else if (loginState === 'LOGGED_IN') {
			setLoginState('LOGIN');
		}
	}, [token]);

	return (
		<IonPage>
			<IonHeader>
				<IonToolbar>

				</IonToolbar>
			</IonHeader>
			<IonContent fullscreen>
				<IonGrid className="ion-padding">
					<IonRow className="ion-justify-content-center">
						<IonCol sizeLg="4">
							{(loginState === 'LOGIN' || isSSO) &&
								<IonCard>
									<IonCardHeader>
										<IonCardTitle>Log in</IonCardTitle>
										<h5>Welcome! Please login to your account.</h5>
									</IonCardHeader>
									<IonCardContent>

										<IonInput label="Username" labelPlacement="floating" fill="solid" onIonChange={(e) => setUser(e?.detail?.value ?? '')} required type='email'
												  placeholder='jon@doe.com'/>

										<IonInput label="Password" labelPlacement="floating" fill="solid" onIonChange={(e) => setPassword(e?.detail?.value)} required type='password'
												  placeholder='********'/>

										<IonButton disabled={!user || !password} className="custom-button" expand="block"
										           onClick={() => handleLogin()}>Login</IonButton>
									</IonCardContent>
									<IonCardContent style={{display: 'flex', justifyContent: 'flex-end'}}>
										<IonButton size={"small"} fill={"clear"} color={"secondary"} onClick={handleForgotPassword}>
											I forgot my password
										</IonButton>
									</IonCardContent>
								</IonCard>
							}
							{loginState === 'RESET' &&
								<IonCard>
									<IonCardHeader>
										<IonCardTitle>Password Reset</IonCardTitle>
										<h5>Enter your username and we will send you a password reset link.</h5>
									</IonCardHeader>
									<IonCardContent>
										<div>
											<IonLabel>Username</IonLabel>
											<IonInput onIonChange={(e) => setPasswordResetEmail(e?.detail?.value ?? '')}
											          value={passwordResetEmail} required type='email' placeholder='Username'/>
										</div>
										<IonButton expand="full" disabled={!passwordResetEmail} onClick={() => sendPasswordResetEmail()}>
											Send Request
										</IonButton>
									</IonCardContent>
									<IonCardContent style={{display: 'flex', justifyContent: 'flex-end'}}>
										<IonButton size={"small"} fill={"clear"} color={"secondary"} onClick={() => setLoginState('LOGIN')}>
											Back to Login
										</IonButton>
									</IonCardContent>
								</IonCard>
							}
							{loginState === 'LOGGED_IN' &&
								<div style={{width: '100%', margin: 'auto'}}>
									<p>Please use the left navigation to find the page you are looking for.</p>
									<p>If you are experiencing issues, you may need to <a onClick={onLogout} style={{cursor: 'pointer'}}>logout</a>.</p>
								</div>
							}
						</IonCol>
					</IonRow>

				</IonGrid>
			</IonContent>
		</IonPage>
	);
};

export default Login;
